import '../css/main.css'

import MicroModal from 'micromodal'

import { initBackgroundVideo } from './utils/backgroundVideo'
import { initFormValidation } from './utils/formValidation'
import { initGalleryCarousel } from './utils/galleryCarousel'
import { initGalleryLightbox } from './utils/galleryLightbox'
import { initGalleryVideo } from './utils/galleryVideo'
import { responsiveHeaderHeight } from './utils/headerHeight'
import { initHighlightCarousel } from './utils/highlightCarousel'
import { initLocationPins } from './utils/locationPins'
import { initNavMenu } from './utils/navMenu'
import { initPlanHotspots } from './utils/planHotspots'
import { initPlanLegend } from './utils/planLegend'
import { initPlanTabs } from './utils/planTabs'
import { initPlanZoom } from './utils/planZoom'
import { initPromoPopup } from './utils/promoPopup'
import { initRecaptcha } from './utils/recaptcha'
import { initScrollAnimations } from './utils/scrollAnimations'
import { initSiteProgress } from './utils/siteProgress'
import { initModalSmoothScroll, initSmoothScroll } from './utils/smoothScroll'

window.addEventListener('DOMContentLoaded', () => {
    responsiveHeaderHeight()

    // Lenis smooth scrolling, synced with ScrollTrigger (null if reduced motion).
    const lenis = initSmoothScroll()

    // The legal modals' scrolling bodies, eased the same way (no-op if reduced motion).
    initModalSmoothScroll()

    // Highlights: photo track sliding, copy track cross-fading, moving as one.
    initHighlightCarousel()

    // Gallery page: the render carousel and the lightbox its thumbs open, the
    // year-tabbed progress carousels, and the poster/play overlay on the film.
    // Each is a no-op off that page.
    initGalleryCarousel()
    initGalleryLightbox()
    initSiteProgress()
    initGalleryVideo()

    // Plans page: the hero callouts, the collection accordion beside the
    // masterplan, every tablist on the page, and the shared zoom dialog. Each is
    // a no-op off that page.
    initPlanHotspots()
    initPlanLegend()
    initPlanTabs()
    initPlanZoom()

    // Location page: the amenity accordion and the map pins it keys. A no-op
    // off that page.
    initLocationPins()

    // Header pill: hover/tap to expand the main menu.
    initNavMenu()

    // GSAP intro + ScrollTrigger reveals/parallax across the page. Settles once
    // the intro has played, which the promo popup waits for below.
    const introDone = initScrollAnimations()

    // Decorative background videos — paused under reduced motion.
    initBackgroundVideo()

    // Register form: keep the browser's validation bubble out from under the
    // fixed header. Shares the Lenis instance so the correction can't be undone
    // by a smooth scroll still in flight.
    initFormValidation(lenis)

    // Register form: fetch a reCAPTCHA token once the form is valid. Must run
    // after initFormValidation so it can see that handler cancel the submit.
    initRecaptcha()

    // Header links and the hero scroll cues: every in-page hash goes to Lenis.
    anchorScrollToSection(lenis)

    // Footer legal modals (disclaimer / T&C / privacy), opened via
    // [data-micromodal-trigger]. awaitCloseAnimation lets the CSS slide-out
    // play before the modal is hidden; modals carry data-lenis-prevent so
    // their inner scroll isn't swallowed by Lenis.
    MicroModal.init({
        openClass: 'is-open',
        disableScroll: true,
        awaitCloseAnimation: true,
        onShow: (modal) => {
            // MicroModal focuses the first non-close focusable node on open;
            // in text-heavy modals that's a link partway down the content,
            // which scrolls the container away from the top. Keep focus in the
            // dialog (the close button) without scrolling, then pin to top.
            modal.querySelector('button[data-micromodal-close]')?.focus({ preventScroll: true })
            const body = modal.querySelector('.modal__body')
            if (body) body.scrollTop = 0
        },
    })

    // Home page: The Big Move campaign banner, on every load, once the intro
    // has played so it doesn't cover the hero mid-animation. Must follow
    // MicroModal.init, whose options it inherits. A no-op off that page.
    introDone.then(initPromoPopup)
})

function anchorScrollToSection(lenis) {
    // Bare hashes only. The header's off-page links are built with home_url() and
    // are absolute, so they navigate normally — which is what they are for.
    //
    // The skip link is excluded deliberately: this handler preventDefaults, and
    // that would cancel the fragment navigation that moves focus into <main>.
    // Scrolling alone is not a skip link — the user would tab straight back into
    // the header. It has nothing to scroll to anyway, <main> starts at the top.
    const anchors = document.querySelectorAll('a[href^="#"]:not(.skip-link)')
    if (!anchors.length) return

    anchors.forEach((anchor) => {
        const hash = anchor.getAttribute('href')
        // getElementById, not querySelector: an id is legal HTML but not always a
        // legal CSS selector — '#360vr' starts with a digit and throws, which
        // would abort this loop and leave the later links unwired.
        const target = document.getElementById(hash.slice(1))
        if (!target) return

        anchor.addEventListener('click', (e) => {
            e.preventDefault()
            // Native smooth scroll fights Lenis's animation loop, so route
            // anchor jumps through Lenis when it's active.
            if (lenis) lenis.scrollTo(target)
            else target.scrollIntoView({ behavior: 'smooth' })
        })
    })
}
